/**
 * useChatPresence — publish "I am in this chat" and read who else is.
 *
 * Returns the OTHER people (never me) grouped one row per person, plus a
 * `setTyping` setter the composer calls. All RTDB failures are swallowed:
 * presence is cosmetic and must never break the chat.
 *
 * Lifecycle per conversation id:
 *   join → (typing/viewing updates, 60 s heartbeat) → leave on switch/unmount
 *
 * Skipped entirely when: no user, no extension, staff impersonation, or
 * `enabled` is false. Placeholder "Start … conversation" rows are NOT
 * skipped: they already carry the id the real conversation will get. In
 * VITE_E2E builds the vite alias swaps the lib module for the in-memory
 * chat-presence.e2e.ts, so no real RTDB is touched.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import {
  joinPresence,
  subscribePresence,
  subscribeServerTimeOffset,
  groupPresence,
  samePresence,
  HEARTBEAT_MS,
  STALE_AFTER_MS,
  type PresenceHandle,
  type PresenceFolder,
  type PresenceScope,
  type PresenceStatus,
  type PresenceUser,
} from '@/lib/firebase/chat-presence';

/** How long I stay 'typing' after the last keystroke. Short, so the chip
 *  and strip on a colleague's screen clear a few seconds after I stop — even
 *  when the unsent text is still in the box. Send / clear / Note mode flip
 *  back to 'viewing' at once (the composer calls setTyping(false)). */
const TYPING_IDLE_MS = 3 * 1000;

const EMPTY: PresenceUser[] = [];

export interface ChatPresenceResult {
  /** Other people in this chat (me excluded), sorted by name. */
  others: PresenceUser[];
  /** Composer hook: true on keystroke, false on send/clear. Idempotent. */
  setTyping: (typing: boolean) => void;
}

export function useChatPresence(conversationId: string | null | undefined, enabled = true): ChatPresenceResult {
  const extensionName = useAppStore((s) => s.extensionName);
  const user = useAuthStore((s) => s.user);
  const impersonatedUserId = useAuthStore((s) => s.impersonatedUserId);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);

  const [raw, setRaw] = useState<PresenceFolder | null>(null);
  const [others, setOthers] = useState<PresenceUser[]>(EMPTY);
  const handleRef = useRef<PresenceHandle | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Status the composer wants right now. Applied once the join resolves, so
   *  a keystroke that lands while the first write is in flight is not lost. */
  const wantedRef = useRef<PresenceStatus>('viewing');
  /** Server clock minus client clock (ms). Stale checks use server time. */
  const offsetRef = useRef(0);

  const uid = user?.uid;

  const active =
    enabled &&
    !!conversationId &&
    !!extensionName &&
    !!uid &&
    !impersonatedUserId;

  // Scope = the account that OWNS the inbox (same uid the conversations are
  // loaded from, see getEffectiveUid): the selected admin for shared users,
  // else my own uid. NOT the CRM org id — a shared user signed in from their
  // own CRM org has a different org id than the admin, so the two would
  // write into different folders and never see each other.
  const orgKey = active && uid ? (selectedAdminUserId || uid) : null;

  const myUid = uid ?? '';

  // ── Server clock offset (one listener for the hook's lifetime) ──────────
  useEffect(() => {
    if (!active) return;
    return subscribeServerTimeOffset((ms) => { offsetRef.current = ms; });
  }, [active]);

  // ── Join / leave + subscribe ─────────────────────────────────────────────
  useEffect(() => {
    if (!active || !orgKey || !conversationId || !extensionName || !user) {
      setRaw(null);
      return;
    }
    const scope: PresenceScope = { extensionName, orgKey, conversationId };
    let cancelled = false;

    const unsubscribe = subscribePresence(scope, (val) => {
      if (!cancelled) setRaw(val);
    });

    // Never publish an email — the folder is world-readable. Accounts with no
    // display name get `displayName = email` from mapFirebaseUser, so keep
    // only the part before the "@".
    const rawName = user.displayName || 'Someone';
    const name = rawName.includes('@') ? (rawName.split('@')[0] || 'Someone') : rawName;

    joinPresence(scope, {
      uid: user.uid,
      name,
      photoURL: user.photoURL,
    }).then((h) => {
      // Conversation switched while the first write was in flight → undo.
      if (cancelled) { void h.leave(); return; }
      handleRef.current = h;
      // Typing started before the join finished → publish it now.
      if (wantedRef.current !== 'viewing') void h.setStatus(wantedRef.current);
    });

    const heartbeat = setInterval(() => { void handleRef.current?.heartbeat(); }, HEARTBEAT_MS);

    return () => {
      cancelled = true;
      clearInterval(heartbeat);
      if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
      wantedRef.current = 'viewing';
      unsubscribe();
      const h = handleRef.current;
      handleRef.current = null;
      void h?.leave();
    };
    // `user` object identity is stable per session; uid covers the real change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, orgKey, conversationId, extensionName, user?.uid]);

  // ── Group raw → others; re-run on a timer so stale nodes age out even
  //    when nobody writes. Keeps the previous array when nothing visible
  //    changed so heartbeats / the timer do not re-render the chat. ────────
  useEffect(() => {
    if (!raw) { setOthers(EMPTY); return; }
    const compute = () => {
      const next = groupPresence(raw, myUid, Date.now() + offsetRef.current);
      setOthers((prev) => (samePresence(prev, next) ? prev : next));
    };
    compute();
    const t = setInterval(compute, STALE_AFTER_MS / 2);
    return () => clearInterval(t);
  }, [raw, myUid]);

  // ── Typing ───────────────────────────────────────────────────────────────
  const setTyping = useCallback((typing: boolean) => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    wantedRef.current = typing ? 'typing' : 'viewing';
    void handleRef.current?.setStatus(wantedRef.current);
    if (typing) {
      idleTimerRef.current = setTimeout(() => {
        wantedRef.current = 'viewing';
        void handleRef.current?.setStatus('viewing');
      }, TYPING_IDLE_MS);
    }
  }, []);

  return { others, setTyping };
}
